import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useTable, useSortBy, useFilters } from 'react-table';
import { Button, Modal } from 'react-bootstrap';
import { addProfessional, updateProfessional, deleteProfessional } from '@/services/schedulingService';
import { ProfessionalName as ProfessionalNameCell } from '@/components/common/ProfessionalPhoto';
import ProfessionalNotes from '@/components/data/professionals/ProfessionalNotes';
import ProfessionalSkills from '@/components/data/professionals/ProfessionalSkills';
import ProfessionalForm from '@/components/data/professionals/ProfessionalForm';

import useInput from '@digital_monorepo/lib-common/hooks/useInput';
import { professionalsCss, buttonCss, modalCss, professionalspageCss } from '@digital_monorepo/styling';

const Professionals = ({ data, onRefresh }) => {
  const { t } = useTranslation();
  const [professionals, setProfessionals] = useState(data || []);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [isAddingProfessional, setIsAddingProfessional] = useState(false);

  useEffect(() => {
    setProfessionals(data || []);
  }, [data]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formHasError, setFormHasError] = useState(false);

  // Form input hooks
  const { value: ProfessionalID, isValid: idIsValid, hasError: idHasError, valueChangeHandler: idChangeHandler, inputBlurHandler: idBlurHandler, reset: resetId } = useInput(value => !!value);

  const {
    value: ProfessionalName,
    isValid: nameIsValid,
    hasError: nameHasError,
    valueChangeHandler: nameChangeHandler,
    inputBlurHandler: nameBlurHandler,
    reset: resetName
  } = useInput(value => !!value);

  const {
    value: FunctionLevel,
    isValid: levelIsValid,
    hasError: levelHasError,
    valueChangeHandler: levelChangeHandler,
    inputBlurHandler: levelBlurHandler,
    reset: resetLevel
  } = useInput(value => !!value);

  const {
    value: FunctionName,
    isValid: functionIsValid,
    hasError: functionHasError,
    valueChangeHandler: functionChangeHandler,
    inputBlurHandler: functionBlurHandler,
    reset: resetFunction
  } = useInput(value => !!value);

  const {
    value: Office,
    isValid: officeIsValid,
    hasError: officeHasError,
    valueChangeHandler: officeChangeHandler,
    inputBlurHandler: officeBlurHandler,
    reset: resetOffice
  } = useInput(value => !!value);

  const { value: ProfessionalRC, isValid: rcIsValid, hasError: rcHasError, valueChangeHandler: rcChangeHandler, inputBlurHandler: rcBlurHandler, reset: resetRc } = useInput(value => !!value);

  const { value: area, isValid: areaIsValid, hasError: areaHasError, valueChangeHandler: areaChangeHandler, inputBlurHandler: areaBlurHandler, reset: resetArea } = useInput(value => !!value);

  const handleStartAddProfessional = () => {
    setIsAddingProfessional(true);
    setFormHasError(false);
    resetId();
    resetName();
    resetLevel();
    resetFunction();
    resetOffice();
    resetRc();
    resetArea();
  };

  const handleUpdateProfessionalField = (professionalID, field, value) => {
    setProfessionals(prevProfessionals => prevProfessionals.map(prof => (prof.ProfessionalID === professionalID ? { ...prof, [field]: value } : prof)));
  };

  const handleEditProfessional = professional => {
    setSelectedProfessional(professional);
    setFormHasError(false);
    idChangeHandler({ target: { value: professional.ProfessionalID || '' } });
    nameChangeHandler({ target: { value: professional.ProfessionalName || '' } });
    levelChangeHandler({ target: { value: professional.FunctionLevel || '' } });
    functionChangeHandler({ target: { value: professional.FunctionName || '' } });
    officeChangeHandler({ target: { value: professional.Office || '' } });
    rcChangeHandler({ target: { value: professional.ProfessionalRC || '' } });
    areaChangeHandler({ target: { value: professional.area || '' } });
  };

  const handleCancel = () => {
    setSelectedProfessional(null);
    setIsAddingProfessional(false);
    setFormHasError(false);
  };

  const validateForm = () => {
    const isValid = nameIsValid && (isAddingProfessional ? idIsValid : true) && levelIsValid && functionIsValid && officeIsValid && rcIsValid && areaIsValid;

    if (!isValid) {
      setFormHasError(true);
    }
    return isValid;
  };

  const handleSave = async event => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormSubmitting(true);

    try {
      const professionalData = {
        ProfessionalID,
        ProfessionalName,
        FunctionLevel,
        FunctionName,
        Office,
        ProfessionalRC,
        area
      };

      if (isAddingProfessional) {
        const createdProfessional = await addProfessional(professionalData);
        setProfessionals(prevProfessionals => [createdProfessional, ...prevProfessionals]);
        setIsAddingProfessional(false);
      } else {
        await updateProfessional(selectedProfessional.ProfessionalID, { ...professionalData });
        setProfessionals(prevProfessionals =>
          prevProfessionals.map(professional => (professional.ProfessionalID === selectedProfessional.ProfessionalID ? { ...professional, ...professionalData } : professional))
        );
        setSelectedProfessional(null);
      }
    } catch (error) {
      console.error('Error saving professional:', error);
      setFormHasError(true);
    } finally {
      setFormSubmitting(false);
    }
  };

  const confirmDelete = professional => {
    setSelectedProfessional(professional);
    setShowDeleteConfirmModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteProfessional(selectedProfessional.ProfessionalID);
      setShowDeleteConfirmModal(false);
      setSelectedProfessional(null);
      // Refetch from the parent so the row is gone for good. Filtering the local
      // copy here is not enough: the sync effect above would put it straight back.
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error deleting professional:', error);
    }
  };

  const getFormData = type => ({
    ProfessionalID,
    ProfessionalName,
    FunctionLevel,
    FunctionName,
    Office,
    ProfessionalRC,
    area,
    idError: idHasError,
    nameError: nameHasError,
    levelError: levelHasError,
    functionError: functionHasError,
    officeError: officeHasError,
    rcError: rcHasError,
    areaError: areaHasError,
    onIdChange: idChangeHandler,
    onNameChange: nameChangeHandler,
    onLevelChange: levelChangeHandler,
    onFunctionChange: functionChangeHandler,
    onOfficeChange: officeChangeHandler,
    onRcChange: rcChangeHandler,
    onAreaChange: areaChangeHandler,
    onIdBlur: idBlurHandler,
    onNameBlur: nameBlurHandler,
    onLevelBlur: levelBlurHandler,
    onFunctionBlur: functionBlurHandler,
    onOfficeBlur: officeBlurHandler,
    onRcBlur: rcBlurHandler,
    onAreaBlur: areaBlurHandler
  });

  const columns = useMemo(
    () => [
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_ID'),
        accessor: 'ProfessionalID',
        filter: 'text',
        Cell: ({ value, row }) => (
          <Link to={`/professionals/${value}`} className={professionalspageCss.linkCell}>
            {value}
          </Link>
        )
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_NAME'),
        accessor: 'ProfessionalName',
        filter: 'text',
        Cell: ({ row }) => (
          <Link to={`/professionals/${row.original.ProfessionalID}`}>
            <ProfessionalNameCell professional={row.original} />
          </Link>
        )
      },
      {
        Header: 'DPN',
        accessor: 'DpnUrl',
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ value, row }) =>
          value && (row.original.area || '').toLowerCase() !== 'contractor' ? (
            <a href={value} target='_blank' rel='noopener noreferrer' className={professionalspageCss.linkCell}>
              View
            </a>
          ) : null
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.FUNCTION_LEVEL'),
        accessor: 'FunctionLevel',
        filter: 'text'
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.FUNCTION_NAME'),
        accessor: 'FunctionName',
        filter: 'text'
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.OFFICE'),
        accessor: 'Office',
        filter: 'text'
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_RC'),
        accessor: 'ProfessionalRC',
        filter: 'text'
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.AREA'),
        accessor: 'area',
        filter: 'text'
      },
      {
        Header: 'Gender',
        accessor: row => row.Gender || 'Not specified',
        id: 'Gender',
        filter: 'text'
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_NOTES'),
        accessor: row => row.ProfessionalNotes || '',
        id: 'ProfessionalNotes',
        filter: 'text',
        Cell: ({ row }) => (
          <ProfessionalNotes
            professionalID={row.original.ProfessionalID}
            professionalNotes={row.original.ProfessionalNotes}
            onUpdate={newValue => handleUpdateProfessionalField(row.original.ProfessionalID, 'ProfessionalNotes', newValue)}
          />
        )
      },
      {
        Header: t('PROFESSIONALS_TABLE.HEADERS.SKILLS'),
        accessor: row => row.Skills || '',
        id: 'Skills',
        filter: 'text',
        Cell: ({ row }) => (
          <ProfessionalSkills
            professionalID={row.original.ProfessionalID}
            skills={row.original.Skills}
            onUpdate={newValue => handleUpdateProfessionalField(row.original.ProfessionalID, 'Skills', newValue)}
          />
        )
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableFilters: true,
        Cell: ({ row }) => (
          <div className={professionalspageCss.actionButtons}>
            <button
              onClick={() => confirmDelete(row.original)}
              disabled={isAddingProfessional || !!selectedProfessional}
              className={`${professionalspageCss.actionButton} ${professionalspageCss.deleteButton}`}>
              Delete
            </button>
          </div>
        )
      }
    ],
    [t, isAddingProfessional, selectedProfessional]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: professionals,
      autoResetFilters: false,
      autoResetSortBy: false
    },
    useFilters,
    useSortBy
  );

  return (
    <div className={professionalsCss.professionalsContainer}>
      {formHasError && (
        <div className='alert alert-danger' role='alert'>
          Please fix the form errors before submitting.
        </div>
      )}

      <div className={professionalspageCss.tableContainer}>
        <div className={professionalspageCss.tableScrollContainer}>
          <table {...getTableProps()} className={professionalspageCss.table}>
            <thead>
              {headerGroups.map((headerGroup, groupIndex) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={groupIndex}>
                  {headerGroup.headers.map((column, colIndex) => (
                    <th {...column.getHeaderProps()} key={colIndex} className={professionalspageCss.th} data-column={column.id}>
                      <div {...column.getSortByToggleProps()} className={professionalspageCss.headerContent}>
                        <span>{column.render('Header')}</span>
                        <span className={professionalspageCss.sortIcon}>{column.isSorted ? (column.isSortedDesc ? '↓' : '↑') : '↕'}</span>
                      </div>
                      {column.canFilter && (
                        <input
                          type='text'
                          value={column.filterValue || ''}
                          onChange={e => column.setFilter(e.target.value || undefined)}
                          placeholder='Filter…'
                          aria-label={`Filter ${column.Header}`}
                          className={professionalspageCss.filterInput}
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    No professionals match the current filters
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={rowIndex} className={professionalspageCss.row}>
                      {row.cells.map((cell, cellIndex) => (
                        <td {...cell.getCellProps()} key={cellIndex} className={professionalspageCss.td} data-column={cell.column.id}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Professional Modal */}
      <Modal
        show={!showDeleteConfirmModal && (isAddingProfessional || !!selectedProfessional)}
        onHide={handleCancel}
        centered
        dialogClassName={professionalsCss.editModal}
        className={`${modalCss['modal-overlay']}`}>
        <div className={modalCss['modal-content']}>
          <Modal.Header closeButton>
            <Modal.Title>{isAddingProfessional ? 'Add Professional' : 'Edit Professional'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ProfessionalForm
              formData={getFormData(isAddingProfessional ? 'add' : 'edit')}
              onSubmit={handleSave}
              onCancel={handleCancel}
              formType={isAddingProfessional ? 'add' : 'edit'}
              isSubmitting={formSubmitting}
              hasError={formHasError}
            />
          </Modal.Body>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirmModal}
        onHide={() => {
          setShowDeleteConfirmModal(false);
          setSelectedProfessional(null);
        }}
        centered
        dialogClassName={professionalsCss.centeredModal}
        className={`${modalCss['modal-overlay']}`}>
        <div className={modalCss['modal-content']}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to delete professional: <strong>{selectedProfessional?.ProfessionalName}</strong>?
            </p>
            <p className='text-danger mt-2'>This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant='secondary'
              onClick={() => {
                setShowDeleteConfirmModal(false);
                setSelectedProfessional(null);
              }}
              className={`${buttonCss.secondaryBtn} ${professionalsCss.adminButton}`}>
              Cancel
            </Button>
            <Button variant='danger' onClick={handleDelete} className={`${buttonCss.primaryBtn} ${professionalsCss.adminButton}`}>
              Delete
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

Professionals.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRefresh: PropTypes.func
};

export default Professionals;
